import './WelfareCard.css';

/**
 * Always-present footer / sidebar callout on every parent screen.
 *
 * Required by §22.13 launch checklist: "Welfare Officer contact visible
 * on every parent screen" — regulatory mandate from §22.12 safeguarding
 * pathway + ICO Children's Code §11 parental controls.
 *
 * Design move: deliberately non-glass. The whole web2 chrome uses glass
 * surfaces; the WelfareCard breaks that convention with an opaque
 * `--surface-1` body + soft `--accent-soft` left-border. The visual
 * dissonance is intentional — this is a calm, always-there safeguarding
 * presence, not a decorative chrome element. Never an alert state.
 *
 * Helpline numbers verified against `docs/legal/safeguarding-operating-
 * model.md` §1 + community-standards.md §4.
 */

export interface WelfareCardProps {
  /** Welfare Officer contact for the active club. Optional — when no
   *  child is selected (or the club has not yet appointed a WO), the
   *  helpline links remain visible with a fallback message. */
  welfareOfficer?: {
    name: string;
    email: string;
  } | null;
  /** Compact variant for narrow shells (mobile-style sidebar). */
  compact?: boolean;
}

export function WelfareCard({ welfareOfficer, compact }: WelfareCardProps) {
  return (
    <aside
      className="welfare-card"
      data-compact={compact ? 'true' : 'false'}
      aria-label="Safeguarding contacts"
    >
      <header className="welfare-card__head">
        <div className="welfare-card__eyebrow">Safeguarding</div>
        <h3 className="welfare-card__title">If something feels wrong</h3>
      </header>

      {welfareOfficer ? (
        <div className="welfare-card__officer">
          <div className="welfare-card__officer-label">Your club's Welfare Officer</div>
          <div className="welfare-card__officer-name">{welfareOfficer.name}</div>
          <a
            href={`mailto:${welfareOfficer.email}`}
            className="welfare-card__officer-email"
          >
            {welfareOfficer.email}
          </a>
        </div>
      ) : (
        <div className="welfare-card__officer welfare-card__officer--placeholder">
          Your club's Welfare Officer details will appear here once a child is selected.
        </div>
      )}

      <ul className="welfare-card__helplines">
        <li>
          <strong>NSPCC</strong> · <a href="tel:08088005000">0808 800 5000</a>
          <span className="welfare-card__helpline-sub">24h, free, confidential</span>
        </li>
        <li>
          <strong>Childline</strong> · <a href="tel:08001111">0800 1111</a>
          <span className="welfare-card__helpline-sub">under-19, free</span>
        </li>
        <li>
          <strong>Samaritans</strong> · <a href="tel:116123">116 123</a>
          <span className="welfare-card__helpline-sub">any age, 24h</span>
        </li>
        <li>
          <strong>YoungMinds Crisis Messenger</strong> · text <code>YM</code> to <code>85258</code>
        </li>
        <li>
          <strong>NHS</strong> · <a href="tel:111">111</a>
        </li>
      </ul>

      <a
        href="mailto:welfare@4cmcoaching.co.uk"
        className="welfare-card__report"
      >
        Report a concern to 4CM
      </a>
    </aside>
  );
}
